import { useQueryClient } from "@tanstack/react-query";
import {
	ChevronRight,
	ChevronsUpDown,
	LogOut,
	Monitor,
	Moon,
	Palette,
	Settings,
	Sun,
} from "lucide-react";
import {
	type ComponentProps,
	type ComponentType,
	useEffect,
	useRef,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { BrandMark } from "@/components/layout/brand-mark";
import { ProjectSwitcher } from "@/components/layout/project-switcher";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	useSidebar,
} from "@/components/ui/sidebar";
import { PendingBadge } from "@/features/presence/components/pending-badge";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useActiveContext } from "@/store/use-active-context";
import { useAuth } from "@/store/use-auth";
import { type Theme, useTheme } from "@/store/use-theme";
import {
	isNavItemActive,
	type NavBadge,
	navItemPath,
	navItems,
	obraGroup,
} from "./nav-items";

const NAV_BADGES: Record<NavBadge, ComponentType> = {
	"pending-attendance": PendingBadge,
};

function ObraNavGroup() {
	const { pathname } = useLocation();
	const { obra: obraKey } = useParams();
	const { has } = usePermissions();
	const subPath = pathname.split("/").slice(2).join("/");
	const items = obraGroup.items.filter(
		(item) => !item.permission || has(item.permission),
	);

	if (items.length === 0) return null;

	const GroupIcon = obraGroup.icon;

	return (
		<Collapsible asChild defaultOpen className="sidebar-collapsible-group">
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton
						tooltip={obraGroup.title}
						className="sidebar-nav-item"
					>
						<GroupIcon />
						<span>{obraGroup.title}</span>
						<ChevronRight className="sidebar-collapsible-group__chevron" />
					</SidebarMenuButton>
				</CollapsibleTrigger>
				<CollapsibleContent>
					<SidebarMenuSub>
						{items.map((item) => (
							<SidebarMenuSubItem key={item.segment}>
								<SidebarMenuSubButton
									asChild
									isActive={isNavItemActive(item.segment, subPath)}
								>
									<Link to={navItemPath(obraKey ?? "", item.segment)}>
										<item.icon />
										<span>{item.title}</span>
									</Link>
								</SidebarMenuSubButton>
							</SidebarMenuSubItem>
						))}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	);
}

function NavItemBadge({ kind }: { kind: NavBadge }) {
	const Badge = NAV_BADGES[kind];
	return <Badge />;
}

function getInitials(name: string) {
	return name
		.split(" ")
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");
}

export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
	const { pathname } = useLocation();
	const { obra: obraKey } = useParams();
	const { isMobile, setOpenMobile } = useSidebar();
	const { has } = usePermissions();
	const subPath = pathname.split("/").slice(2).join("/");
	const visibleNavItems = navItems.filter(
		(item) => !item.permission || has(item.permission),
	);
	const lastPath = useRef(pathname);

	useEffect(() => {
		if (pathname === lastPath.current) return;
		lastPath.current = pathname;
		if (isMobile) setOpenMobile(false);
	}, [pathname, isMobile, setOpenMobile]);

	return (
		<Sidebar collapsible="icon" className="app-sidebar" {...props}>
			{isMobile && (
				<SidebarHeader>
					<BrandMark />
				</SidebarHeader>
			)}

			<SidebarContent>
				<div className="app-sidebar__switcher">
					<ProjectSwitcher />
				</div>

				<SidebarGroup>
					<SidebarMenu>
						{visibleNavItems.map((item) => (
							<SidebarMenuItem key={item.segment || "dashboard"}>
								<SidebarMenuButton
									asChild
									tooltip={item.title}
									isActive={isNavItemActive(item.segment, subPath)}
									className="sidebar-nav-item"
								>
									<Link to={navItemPath(obraKey ?? "", item.segment)}>
										<item.icon />
										<span>{item.title}</span>
									</Link>
								</SidebarMenuButton>
								{item.badge && <NavItemBadge kind={item.badge} />}
							</SidebarMenuItem>
						))}
						<ObraNavGroup />
					</SidebarMenu>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter>
				{has(["stage_templates:list", "worksites:list"]) && (
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton
								asChild
								tooltip="Configurações"
								isActive={
									subPath === "configuracoes" ||
									subPath.startsWith("configuracoes/")
								}
								className="sidebar-nav-item"
							>
								<Link to={navItemPath(obraKey ?? "", "configuracoes")}>
									<Settings />
									<span>Configurações</span>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				)}
				<SidebarUserMenu />
			</SidebarFooter>

			<SidebarRail />
		</Sidebar>
	);
}

function SidebarUserMenu() {
	const { isMobile } = useSidebar();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const session = useAuth((state) => state.session);
	const signOut = useAuth((state) => state.signOut);
	const resetObra = useActiveContext((state) => state.reset);
	const theme = useTheme((state) => state.theme);
	const setTheme = useTheme((state) => state.setTheme);

	const displayName = session?.user.name?.trim() || session?.user.email || "";
	const email = session?.user.email ?? "";

	function handleLogout() {
		signOut();
		resetObra();
		queryClient.clear();
		navigate("/login");
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton size="lg" className="sidebar-user-menu__trigger">
							<Avatar className="sidebar-user-menu__avatar">
								<AvatarFallback className="sidebar-user-menu__avatar-fallback">
									{getInitials(displayName)}
								</AvatarFallback>
							</Avatar>
							<span className="sidebar-user-menu__identity">
								<span className="sidebar-user-menu__name">{displayName}</span>
								<span className="sidebar-user-menu__email">{email}</span>
							</span>
							<ChevronsUpDown className="sidebar-user-menu__chevron" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="sidebar-user-menu__menu"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="sidebar-user-menu__menu-label">
							<span className="sidebar-user-menu__menu-identity">
								<span className="sidebar-user-menu__name">{displayName}</span>
								<span className="sidebar-user-menu__email">{email}</span>
							</span>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>
								<Palette />
								Tema
							</DropdownMenuSubTrigger>
							<DropdownMenuSubContent>
								<DropdownMenuRadioGroup
									value={theme}
									onValueChange={(value) => setTheme(value as Theme)}
								>
									<DropdownMenuRadioItem value="light">
										<Sun />
										Claro
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="dark">
										<Moon />
										Escuro
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="system">
										<Monitor />
										Sistema
									</DropdownMenuRadioItem>
								</DropdownMenuRadioGroup>
							</DropdownMenuSubContent>
						</DropdownMenuSub>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={handleLogout}>
							<LogOut />
							Sair
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
